import { createContext, useEffect, useReducer } from 'react';
import PropTypes from 'prop-types';
// utils
import axios from '../api/axiosInstance';
import { setSession, isValidUser } from '../utils/jwt';
import { endpoints } from '../api/endpoints';
import { post } from 'src/api/requests';
import { toast } from 'react-toastify';
const queryString = require('query-string');

// ----------------------------------------------------------------------

const initialState = {
  isAuthenticated: false,
  isInitialized: false,
  user: null,
};

const handlers = {
  INITIALIZE: (state, action) => {
    const { isAuthenticated, user } = action.payload;
    return {
      ...state,
      isAuthenticated,
      isInitialized: true,
      user,
    };
  },
  LOGIN: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      isInitialized: true,
      user,
    };
  },
  LOGOUT: (state) => ({
    ...state,
    isAuthenticated: false,
    user: null,
  }),
  REGISTER: (state, action) => {
    const { user } = action.payload;

    return {
      ...state,
      isAuthenticated: true,
      user,
    };
  },
};

const reducer = (state, action) => (handlers[action.type] ? handlers[action.type](state, action) : state);

const AuthContext = createContext({
  ...initialState,
  method: 'jwt',
  login: () => Promise.resolve(),
  client_login: () => Promise.resolve(),
  silent_login: () => Promise.resolve(),
  update: () => Promise.resolve(),
  logout: () => Promise.resolve(),
  register: () => Promise.resolve(),
});

const getSafeReturnURL = (rawUrl) => {
  if (!rawUrl || typeof rawUrl !== 'string') return null;

  const trimmed = rawUrl.trim();
  if (!trimmed) return null;

  // Reject script schemes
  if (/^(javascript|data|vbscript):/i.test(trimmed)) {
    return null;
  }

  // Reject protocol-relative or backslash-prefixed evasions
  if (trimmed.startsWith('//') || trimmed.startsWith('/\\') || trimmed.startsWith('\\')) {
    return null;
  }

  // Parse every candidate with new URL against currentOrigin and check parsed.origin
  try {
    const currentOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';

    const parsed = new URL(trimmed, currentOrigin);

    if (
      (parsed.protocol === 'http:' || parsed.protocol === 'https:') &&
      parsed.origin === currentOrigin
    ) {
      return parsed.pathname + parsed.search + parsed.hash;
    }
  } catch (e) {
    return null;
  }

  return null;
};

const getReturnPathname = (safeUrl) => {
  if (!safeUrl || typeof safeUrl !== 'string') return '';
  try {
    const currentOrigin =
      typeof window !== 'undefined' && window.location?.origin
        ? window.location.origin
        : 'http://localhost';
    return new URL(safeUrl, currentOrigin).pathname;
  } catch (e) {
    return '';
  }
};

const resolveRouteEvent = (activeEv, userObj) => {
  if (activeEv && typeof activeEv === 'string' && activeEv.trim()) {
    const trimmed = activeEv.trim();
    if (isNaN(Number(trimmed))) {
      return trimmed;
    }
  }

  const candidateSlug =
    userObj?.event ||
    userObj?.event_code ||
    userObj?.short_code ||
    userObj?.slug ||
    userObj?.event_slug;
  if (candidateSlug && typeof candidateSlug === 'string' && candidateSlug.trim()) {
    const trimmed = candidateSlug.trim();
    if (isNaN(Number(trimmed))) {
      return trimmed;
    }
  }

  if (userObj?.event_id !== undefined && userObj?.event_id !== null) {
    const rawId = String(userObj.event_id).trim();
    if (rawId && isNaN(Number(rawId))) {
      return rawId;
    }

    try {
      const cached = JSON.parse(localStorage.getItem('arsh:event'));
      if (cached && (String(cached.id) === rawId || String(cached.event_id) === rawId)) {
        const resolved = cached.short_code || cached.event_code || cached.slug || cached.event;
        if (resolved && typeof resolved === 'string' && resolved.trim()) {
          const trimmedResolved = resolved.trim();
          if (isNaN(Number(trimmedResolved))) {
            return trimmedResolved;
          }
        }
      }
    } catch (e) {
      // ignore
    }
  }

  try {
    const storedActive = localStorage.getItem('arsh:activeevent');
    if (storedActive && typeof storedActive === 'string' && storedActive.trim()) {
      const trimmedStored = storedActive.trim();
      if (isNaN(Number(trimmedStored))) {
        return trimmedStored;
      }
    }
  } catch (e) {
    // ignore
  }

  return null;
};

// ----------------------------------------------------------------------

AuthProvider.propTypes = {
  children: PropTypes.node,
};

function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(reducer, initialState);

  useEffect(() => {
    const initialize = async () => {
      try {
        const accessToken = localStorage.getItem('accessToken');
        const userStr = localStorage.getItem('arsh:user');

        if (accessToken && userStr && userStr !== 'undefined' && userStr !== 'null') {
          let user = null;
          try {
            user = JSON.parse(userStr);
          } catch (e) {
            user = null;
          }

          if (isValidUser(user)) {
            setSession(accessToken, user);
            dispatch({
              type: 'INITIALIZE',
              payload: {
                isAuthenticated: true,
                user,
              },
            });
            return;
          }
        }

        // Invalid, null, or missing user/token: wipe stale data and initialize as unauthenticated
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      } catch (err) {
        console.error('Failed to initialize auth state:', err);
        setSession(null);
        dispatch({
          type: 'INITIALIZE',
          payload: {
            isAuthenticated: false,
            user: null,
          },
        });
      }
    };

    initialize();
  }, []);

  const login = async (email, password, show_joining_information, activeEvent) => {
    const response = await axios.post(endpoints?.login, {
      email,
      password,
      show_joining_information,
      event: activeEvent,
    });
    const { accessToken, user } = response.data;

    if (!accessToken || !isValidUser(user)) {
      throw new Error('Login failed: invalid user data received');
    }

    setSession(accessToken, user);

    dispatch({
      type: 'LOGIN',
      payload: {
        user,
      },
    });

    const isApp = window.location.search.includes('app') && window.location.search.includes('code');

    const appSuccessFn = () => {
      const resolveAfter3Sec = new Promise((resolve) => setTimeout(resolve, 8000));
      toast.promise(resolveAfter3Sec, {
        pending: 'Please wait, you are being redirected....',
        success: 'Login successful...',
        error: 'Please wait..',
      });
    };

    if (isApp) {
      appSuccessFn();

      const { code } = queryString.parse(window.location.search);
      post(
        endpoints.update893,
        { code, email, event: activeEvent },
        () => appSuccessFn(),
        () => {
          console.log('failed to notify app');
        },
        true
      );
    } else {
      const hasRedirect = window.location.search.includes("returnURL=");
      const { returnURL } = queryString.parse(window.location.search);
      const safeReturnURL = hasRedirect ? getSafeReturnURL(returnURL) : null;
      const returnPathname = getReturnPathname(safeReturnURL);

      const eventToStore = resolveRouteEvent(activeEvent, user);
      if (eventToStore) {
        localStorage.setItem('arsh:activeevent', eventToStore);
      }
      const targetEvent = eventToStore;

      if (user?.userType === 'admin') {
        if (safeReturnURL && (returnPathname === '/admin' || returnPathname.startsWith('/admin/'))) {
          window.location.href = safeReturnURL;
        } else {
          window.location.href = '/admin';
        }
      } else if (user?.userType === 'speaker') {
        if (
          safeReturnURL &&
          (returnPathname.startsWith('/speaker') ||
            returnPathname.includes('/speaker-dashboard') ||
            /\/[^/]+\/speaker(-dashboard)?(\/|$)/.test(returnPathname))
        ) {
          window.location.href = safeReturnURL;
        } else if (targetEvent) {
          window.location.href = `/${targetEvent}/speaker-dashboard`;
        } else {
          window.location.href = '/';
        }
      } else if (user?.userType === 'exhibitor') {
        if (
          safeReturnURL &&
          (returnPathname.startsWith('/exhibitor-dashboard') || returnPathname.startsWith('/exhibitor'))
        ) {
          window.location.href = safeReturnURL;
        } else {
          window.location.href = `/exhibitor-dashboard/home`;
        }
      } else if (user?.userType === 'client') {
        if (safeReturnURL && (returnPathname === '/client' || returnPathname.startsWith('/client/'))) {
          window.location.href = safeReturnURL;
        } else {
          window.location.href = `/client/app`;
        }
      } else if (safeReturnURL) {
        window.location.href = safeReturnURL;
      } else if (targetEvent) {
        window.location.href = `/${targetEvent}/event`;
      } else {
        window.location.href = '/';
      }
    }
  };

  const silent_login = async (event) => {
    try {
      const response = await axios.post(endpoints?.client_silent_login, {
        event,
      });
      const responseData = response?.data?.data || response?.data || {};
      const accessToken = responseData?.accessToken || responseData?.token;
      const user = responseData?.user;

      if (accessToken && isValidUser(user)) {
        setSession(accessToken, user);
        localStorage.setItem('arsh:activeevent', event);
        window.location.href = '/admin';
      } else {
        console.error('Silent login failed: missing token or valid user data');
        setSession(null);
        dispatch({ type: 'LOGOUT' });
        window.location.href = `/${event || ''}/login?returnURL=/admin`;
      }
    } catch (err) {
      console.error('Silent login error:', err);
      setSession(null);
      dispatch({ type: 'LOGOUT' });
      window.location.href = `/${event || ''}/login?returnURL=/admin`;
    }
  };

  const client_login = async (email, password) => {
    const response = await axios.post(endpoints?.client_login, {
      email,
      password,
    });
    const { accessToken, user } = response.data;

    if (accessToken && isValidUser(user)) {
      setSession(accessToken, user);

      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });
    } else {
      throw new Error('Client login failed: invalid user data received');
    }
  };

  const update = async (user) => {
    if (isValidUser(user)) {
      setSession('ewko0i', user);

      dispatch({
        type: 'LOGIN',
        payload: {
          user,
        },
      });

      setTimeout(() => {
        window.location.href = '';
      }, 5000);
    }
  };

  const register = async (email, password, firstName, lastName) => {
    const response = await axios.post('/api/account/register', {
      email,
      password,
      firstName,
      lastName,
    });
    const { accessToken, user } = response.data;

    if (accessToken && isValidUser(user)) {
      setSession(accessToken, user);
      dispatch({
        type: 'REGISTER',
        payload: {
          user,
        },
      });
    } else {
      throw new Error('Registration failed: invalid user data received');
    }
  };

  const logout = async () => {
    setSession(null);
    dispatch({ type: 'LOGOUT' });
  };

  return (
    <AuthContext.Provider
      value={{
        ...state,
        method: 'jwt',
        login,
        silent_login,
        client_login,
        update,
        logout,
        register,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export { AuthContext, AuthProvider };
